import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ArrowLeft, Save, Download, Sparkles, Plus, X, Star, Loader2, Image as ImageIcon } from "lucide-react";
import ResumePreview from "@/components/ResumePreview";
import PaywallModal from "@/components/PaywallModal";

const FONT_OPTIONS = [
  { id: "font-plex-r", label: "IBM Plex Sans" },
  { id: "font-helv-r", label: "Helvetica" },
  { id: "font-serif-r", label: "Playfair (Serif)" },
  { id: "font-slab-r", label: "Merriweather (Slab)" },
  { id: "font-mono-r", label: "Space Mono" },
];
const ACCENT_OPTIONS = ["#0047AB", "#0A0A0A", "#E11D48", "#059669", "#7C3AED", "#B45309"];
const PAPER_OPTIONS = [
  { id: "A4", label: "A4" },
  { id: "Letter", label: "US Letter" },
];
const SPACING_OPTIONS = [
  { id: "compact", label: "Compact" },
  { id: "cozy", label: "Cozy" },
  { id: "airy", label: "Airy" },
];

const Field = ({ label, ...props }) => (
  <label className="block">
    <span className="label-eyebrow">{label}</span>
    <input {...props} className="mt-1 w-full brut-border bg-white px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#0047AB]" />
  </label>
);
const Area = ({ label, ...props }) => (
  <label className="block">
    <span className="label-eyebrow">{label}</span>
    <textarea {...props} className="mt-1 w-full brut-border bg-white px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#0047AB] min-h-[90px]" />
  </label>
);

export default function Editor() {
  const { id } = useParams();
  const { user, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [resume, setResume] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [saving, setSaving] = useState(false);
  const [showPaywall, setShowPaywall] = useState(false);
  const [showMobilePreview, setShowMobilePreview] = useState(false);
  const [aiBusy, setAiBusy] = useState("");
  const debRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const [r, t] = await Promise.all([api.get(`/resumes/${id}`), api.get("/templates")]);
        setResume(r.data);
        setTemplates(t.data);
      } catch {
        toast.error("Failed to load");
        navigate("/dashboard");
      }
    })();
  }, [id, navigate]);

  const updateData = (patch) => setResume((r) => ({ ...r, data: typeof patch === "function" ? patch(r.data) : { ...r.data, ...patch } }));
  const updateStyle = (patch) => setResume((r) => ({ ...r, style: { ...r.style, ...patch } }));
  const setPersonal = (key, value) => setResume((r) => ({ ...r, data: { ...r.data, personal: { ...(r.data.personal || {}), [key]: value } } }));
  const setListItem = (list, index, patch) => setResume((r) => {
    const arr = [...(r.data[list] || [])];
    arr[index] = { ...arr[index], ...patch };
    return { ...r, data: { ...r.data, [list]: arr } };
  });
  const removeListItem = (list, index) => setResume((r) => ({ ...r, data: { ...r.data, [list]: (r.data[list] || []).filter((_, j) => j !== index) } }));
  const addListItem = (list, item) => setResume((r) => ({ ...r, data: { ...r.data, [list]: [...(r.data[list] || []), item] } }));

  // Debounced autosave
  useEffect(() => {
    if (!resume) return;
    if (debRef.current) clearTimeout(debRef.current);
    debRef.current = setTimeout(async () => {
      try {
        setSaving(true);
        await api.put(`/resumes/${resume.resume_id}`, {
          title: resume.title, template: resume.template, data: resume.data, style: resume.style,
        });
      } catch {}
      finally { setSaving(false); }
    }, 700);
    return () => clearTimeout(debRef.current);
  }, [resume]);

  const runAI = async (section, context, apply) => {
    setAiBusy(section);
    try {
      const { data } = await api.post("/ai/generate", { section, context });
      apply(data.text);
      toast.success("Draft ready");
    } catch (e) {
      toast.error("AI draft failed");
    } finally {
      setAiBusy("");
    }
  };

  const uploadImage = async (file, onDone) => {
    if (!user?.is_pro && file.size > 500 * 1024) { setShowPaywall(true); return; }
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
      onDone(url);
    } catch { toast.error("Upload failed"); }
  };

  const exportPDF = () => window.print();

  if (!resume) return <div className="min-h-screen flex items-center justify-center font-display text-lg">Loading editor…</div>;

  const d = resume.data || {};
  const s = resume.style || {};

  const requirePro = (fn) => (...args) => {
    if (!user?.is_pro) { setShowPaywall(true); return; }
    fn(...args);
  };

  return (
    <div className="min-h-screen">
      {/* TOPBAR */}
      <nav className="bg-white sticky top-0 z-30 no-print" style={{ borderBottom: "1px solid #0A0A0A" }}>
        <div className="max-w-[1600px] mx-auto px-4 py-3 flex items-center gap-3">
          <Link to="/dashboard" className="brut-border px-2 py-1.5 text-sm flex items-center gap-1" data-testid="back-btn"><ArrowLeft className="w-4 h-4" /></Link>
          <input
            data-testid="resume-title-input"
            value={resume.title}
            onChange={(e) => setResume({ ...resume, title: e.target.value })}
            className="font-display text-xl font-black bg-transparent focus:outline-none min-w-0 flex-1"
          />
          <div className="hidden sm:flex items-center gap-2 label-eyebrow text-neutral-500">
            {saving ? <><Loader2 className="w-3 h-3 animate-spin" /> Saving</> : <><Save className="w-3 h-3" /> Saved</>}
          </div>
          <button data-testid="mobile-preview-toggle" onClick={() => setShowMobilePreview((v) => !v)} className="lg:hidden brut-border px-3 py-1.5 text-sm">{showMobilePreview ? "Edit" : "Preview"}</button>
          {!user?.is_pro && (
            <button data-testid="upgrade-editor-btn" onClick={() => setShowPaywall(true)} className="brut-border brut-shadow bg-[#FFD700] px-3 py-1.5 text-sm flex items-center gap-1"><Star className="w-4 h-4" /> Pro</button>
          )}
          <button data-testid="export-pdf-btn" onClick={exportPDF} className="brut-border brut-shadow bg-black text-white px-4 py-1.5 text-sm flex items-center gap-1"><Download className="w-4 h-4" /> Export PDF</button>
        </div>
      </nav>

      {/* MAIN LAYOUT */}
      <div className="grid grid-cols-1 lg:grid-cols-12 max-w-[1600px] mx-auto">
        {/* LEFT PANEL - CONTROLS */}
        <aside className={`lg:col-span-5 xl:col-span-4 no-print ${showMobilePreview ? "hidden" : "block"} lg:block p-6 space-y-8 border-r`} style={{ borderRight: "1px solid #0A0A0A", background: "#F4F4F0" }}>
          {/* STYLE */}
          <section>
            <div className="label-eyebrow mb-3">Style</div>
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="text-xs text-neutral-600">Font</span>
                <select data-testid="font-select" value={s.fontFamily} onChange={(e) => updateStyle({ fontFamily: e.target.value })} className="mt-1 w-full brut-border bg-white px-2 py-2">
                  {FONT_OPTIONS.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="text-xs text-neutral-600">Font size</span>
                <input data-testid="fontsize-input" type="number" min="8" max="14" value={s.fontSize} onChange={(e) => updateStyle({ fontSize: parseInt(e.target.value) || 10 })} className="mt-1 w-full brut-border bg-white px-2 py-2" />
              </label>
              <label className="block col-span-2">
                <span className="text-xs text-neutral-600">Accent color</span>
                <div className="mt-2 flex gap-2 flex-wrap" data-testid="accent-picker">
                  {ACCENT_OPTIONS.map((c) => (
                    <button key={c} onClick={() => updateStyle({ accent: c })} className={`w-8 h-8 brut-border ${s.accent === c ? "brut-shadow" : ""}`} style={{ background: c }} aria-label={c} />
                  ))}
                  <input type="color" value={s.accent} onChange={(e) => updateStyle({ accent: e.target.value })} className="w-10 h-8 brut-border cursor-pointer" data-testid="custom-color" />
                </div>
              </label>
              <label className="block">
                <span className="text-xs text-neutral-600">Paper</span>
                <select value={s.paper} onChange={(e) => updateStyle({ paper: e.target.value })} className="mt-1 w-full brut-border bg-white px-2 py-2">
                  {PAPER_OPTIONS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="text-xs text-neutral-600">Spacing</span>
                <select value={s.spacing} onChange={(e) => updateStyle({ spacing: e.target.value })} className="mt-1 w-full brut-border bg-white px-2 py-2">
                  {SPACING_OPTIONS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
                </select>
              </label>
            </div>
          </section>

          {/* TEMPLATE */}
          <section>
            <div className="label-eyebrow mb-3">Template</div>
            <div className="grid grid-cols-3 gap-2">
              {templates.map((t) => {
                const locked = t.pro && !user?.is_pro;
                return (
                  <button
                    key={t.id}
                    data-testid={`template-${t.id}`}
                    onClick={() => locked ? setShowPaywall(true) : setResume((r) => ({ ...r, template: t.id, style: { ...r.style, accent: t.accent } }))}
                    className={`brut-border p-2 text-left ${resume.template === t.id ? "bg-black text-white" : "bg-white"}`}
                  >
                    <div className="text-xs font-bold truncate">{t.name}</div>
                    <div className="mt-2 h-6 flex items-center justify-between">
                      <span className="w-4 h-4" style={{ background: t.accent }} />
                      {t.pro && <span className="bg-[#FFD700] text-black label-eyebrow px-1">PRO</span>}
                    </div>
                  </button>
                );
              })}
            </div>
          </section>

          {/* PERSONAL */}
          <section className="space-y-3">
            <div className="label-eyebrow">Personal</div>
            <Field data-testid="input-name" label="Name" value={d.personal?.name || ""} onChange={(e) => setPersonal("name", e.target.value)} />
            <Field data-testid="input-title" label="Title" value={d.personal?.title || ""} onChange={(e) => setPersonal("title", e.target.value)} />
            <div className="grid grid-cols-2 gap-3">
              <Field label="Email" value={d.personal?.email || ""} onChange={(e) => setPersonal("email", e.target.value)} />
              <Field label="Phone" value={d.personal?.phone || ""} onChange={(e) => setPersonal("phone", e.target.value)} />
              <Field label="Location" value={d.personal?.location || ""} onChange={(e) => setPersonal("location", e.target.value)} />
              <Field label="Website" value={d.personal?.website || ""} onChange={(e) => setPersonal("website", e.target.value)} />
            </div>
            <div>
              <span className="label-eyebrow">Headshot {user?.is_pro ? "" : "(Pro)"}</span>
              <div className="mt-1 flex items-center gap-3">
                {d.photo && <img src={d.photo} alt="me" className="w-14 h-14 object-cover brut-border" />}
                <label className="brut-border px-3 py-2 text-sm cursor-pointer inline-flex items-center gap-2" data-testid="upload-headshot">
                  <ImageIcon className="w-4 h-4" /> Upload
                  <input type="file" accept="image/*" className="hidden" onChange={requirePro((e) => e.target.files?.[0] && uploadImage(e.target.files[0], (url) => updateData({ photo: url })))} />
                </label>
                {d.photo && <button onClick={() => updateData({ photo: null })} className="text-xs underline">Remove</button>}
              </div>
            </div>
          </section>

          {/* SUMMARY */}
          <section className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="label-eyebrow">Summary</div>
              <button
                data-testid="ai-summary-btn"
                onClick={() => runAI("summary", { role: d.personal?.title, industry: "", skills: (d.skills || []).join(", ") }, (text) => updateData({ summary: text }))}
                className="brut-border px-2 py-1 text-xs bg-[#0047AB] text-white inline-flex items-center gap-1"
                disabled={aiBusy === "summary"}
              >
                {aiBusy === "summary" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />} Draft with AI
              </button>
            </div>
            <Area data-testid="input-summary" label="" value={d.summary || ""} onChange={(e) => updateData({ summary: e.target.value })} />
          </section>

          {/* EXPERIENCE */}
          <section className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="label-eyebrow">Experience</div>
              <button data-testid="add-experience" onClick={() => addListItem("experience", { role: "", company: "", period: "", bullets: "" })} className="brut-border px-2 py-1 text-xs inline-flex items-center gap-1"><Plus className="w-3 h-3" /> Add</button>
            </div>
            {(d.experience || []).map((e, i) => (
              <div key={i} className="bg-white brut-border p-3 space-y-2">
                <div className="flex justify-between items-center">
                  <div className="text-xs text-neutral-500">Role #{i + 1}</div>
                  <button onClick={() => removeListItem("experience", i)} className="brut-border px-1 py-0.5 text-xs"><X className="w-3 h-3" /></button>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Field label="Role" value={e.role} onChange={(ev) => setListItem("experience", i, { role: ev.target.value })} />
                  <Field label="Company" value={e.company} onChange={(ev) => setListItem("experience", i, { company: ev.target.value })} />
                </div>
                <Field label="Period" value={e.period} onChange={(ev) => setListItem("experience", i, { period: ev.target.value })} />
                <div className="flex items-center justify-between">
                  <span className="label-eyebrow">Bullets (one per line)</span>
                  <button
                    data-testid={`ai-bullets-${i}`}
                    onClick={() => runAI("experience_bullets", { role: e.role, company: e.company, context: e.bullets }, (text) => setListItem("experience", i, { bullets: text }))}
                    className="brut-border px-2 py-1 text-xs bg-[#0047AB] text-white inline-flex items-center gap-1"
                    disabled={aiBusy === "experience_bullets"}
                  >
                    {aiBusy === "experience_bullets" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />} Draft
                  </button>
                </div>
                <textarea value={e.bullets} onChange={(ev) => setListItem("experience", i, { bullets: ev.target.value })} className="w-full brut-border bg-white px-3 py-2 min-h-[100px]" />
              </div>
            ))}
          </section>

          {/* EDUCATION */}
          <section className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="label-eyebrow">Education</div>
              <button data-testid="add-education" onClick={() => addListItem("education", { school: "", degree: "", period: "" })} className="brut-border px-2 py-1 text-xs inline-flex items-center gap-1"><Plus className="w-3 h-3" /> Add</button>
            </div>
            {(d.education || []).map((e, i) => (
              <div key={i} className="bg-white brut-border p-3 space-y-2">
                <div className="flex justify-between items-center">
                  <div className="text-xs text-neutral-500">School #{i + 1}</div>
                  <button onClick={() => removeListItem("education", i)} className="brut-border px-1 py-0.5 text-xs"><X className="w-3 h-3" /></button>
                </div>
                <Field label="School" value={e.school} onChange={(ev) => setListItem("education", i, { school: ev.target.value })} />
                <div className="grid grid-cols-2 gap-2">
                  <Field label="Degree" value={e.degree} onChange={(ev) => setListItem("education", i, { degree: ev.target.value })} />
                  <Field label="Period" value={e.period} onChange={(ev) => setListItem("education", i, { period: ev.target.value })} />
                </div>
              </div>
            ))}
          </section>

          {/* SKILLS */}
          <section className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="label-eyebrow">Skills</div>
              <button
                data-testid="ai-skills-btn"
                onClick={() => runAI("skills", { role: d.personal?.title, industry: "" }, (text) => updateData({ skills: text.split(",").map((s) => s.trim()).filter(Boolean) }))}
                className="brut-border px-2 py-1 text-xs bg-[#0047AB] text-white inline-flex items-center gap-1"
                disabled={aiBusy === "skills"}
              >
                {aiBusy === "skills" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />} Draft with AI
              </button>
            </div>
            <textarea
              data-testid="input-skills"
              value={(d.skills || []).join(", ")}
              onChange={(e) => updateData({ skills: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
              placeholder="React, TypeScript, System Design…"
              className="w-full brut-border bg-white px-3 py-2 min-h-[70px]"
            />
          </section>

          {/* CERTIFICATES */}
          <section className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="label-eyebrow">Certificates {user?.is_pro ? "" : "(Pro for images)"}</div>
              <button data-testid="add-certificate" onClick={() => addListItem("certificates", { name: "", issuer: "", year: "", image: null })} className="brut-border px-2 py-1 text-xs inline-flex items-center gap-1"><Plus className="w-3 h-3" /> Add</button>
            </div>
            {(d.certificates || []).map((c, i) => (
              <div key={i} className="bg-white brut-border p-3 space-y-2">
                <div className="flex justify-between items-center">
                  <div className="text-xs text-neutral-500">Certificate #{i + 1}</div>
                  <button onClick={() => removeListItem("certificates", i)} className="brut-border px-1 py-0.5 text-xs"><X className="w-3 h-3" /></button>
                </div>
                <Field label="Name" value={c.name} onChange={(ev) => setListItem("certificates", i, { name: ev.target.value })} />
                <div className="grid grid-cols-2 gap-2">
                  <Field label="Issuer" value={c.issuer} onChange={(ev) => setListItem("certificates", i, { issuer: ev.target.value })} />
                  <Field label="Year" value={c.year} onChange={(ev) => setListItem("certificates", i, { year: ev.target.value })} />
                </div>
                <div className="flex items-center gap-3">
                  {c.image && <img src={c.image} alt="cert" className="w-12 h-12 object-cover brut-border" />}
                  <label className="brut-border px-3 py-1.5 text-xs cursor-pointer inline-flex items-center gap-1">
                    <ImageIcon className="w-3 h-3" /> Upload image
                    <input type="file" accept="image/*" className="hidden" onChange={requirePro((e) => e.target.files?.[0] && uploadImage(e.target.files[0], (url) => setListItem("certificates", i, { image: url })))} />
                  </label>
                </div>
              </div>
            ))}
          </section>
        </aside>

        {/* RIGHT PANEL - PREVIEW */}
        <section className={`lg:col-span-7 xl:col-span-8 bg-neutral-200 p-4 md:p-8 flex justify-center print-container ${showMobilePreview ? "block" : "hidden"} lg:block`}>
          <div style={{ transform: "scale(0.7)", transformOrigin: "top center" }} className="hidden md:block">
            <ResumePreview data={d} style={s} />
          </div>
          <div className="md:hidden w-full overflow-x-auto">
            <div style={{ transform: "scale(0.5)", transformOrigin: "top left", width: "210mm" }}>
              <ResumePreview data={d} style={s} />
            </div>
          </div>
        </section>
      </div>

      {showPaywall && (
        <PaywallModal
          onClose={() => setShowPaywall(false)}
          onSuccess={async () => { await checkAuth(); setShowPaywall(false); toast.success("Welcome to Pro!"); }}
        />
      )}
    </div>
  );
}
